import {
  AutocompleteInput, Create, ReferenceInput, SelectInput, SimpleForm, TextInput, TopToolbar,
  required, useGetList, useNotify, useRedirect
} from 'react-admin';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { BackLink } from './back';

/**
 * Adding a product.
 *
 * Only what a product cannot exist without: what it is, what it is called, and
 * who makes it. Prices, categories, compatible printers and images all live on
 * the edit screen behind their own tabs, so this saves and goes straight
 * there rather than growing into a second copy of that form.
 *
 * Zaradenie is required, and that is the whole point of it being here. It was
 * left to the edit screen at first; the first product added this way went out
 * without one and was missing from the Tonery tab and from every filtered
 * search, while looking entirely correct in the admin.
 *
 * The notice at the top is load-bearing. A product does not appear on the shop
 * until it has a price - the storefront's query inner-joins `price` - so
 * saving here is safe, and the thing people need to know is the opposite of a
 * warning: the product is invisible until they finish pricing it.
 */

const TYPES = [
  { id: 'toner', name: 'Toner / náplň' },
  { id: 'printer', name: 'Tlačiareň' },
  { id: 'other', name: 'Iný produkt' }
];

/** Exactly the members of the warehouse_status enum; anything else is a DB error. */
const WAREHOUSE_STATUSES = [
  'skladom', 'do 3 dní', 'do týždňa', 'do 2 týždňov', 'do mesiaca', 'viac ako mesiac', 'info v obchode'
].map(value => ({ id: value, name: value }));

/**
 * Mirrors the server's slugify so the field can be filled in as you type.
 *
 * Drift is harmless: whatever is submitted is slugified again on the server,
 * which is what actually decides the stored value. This exists so the address
 * is visible and editable before saving, not to be the authority on it.
 */
function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 255);
}

/**
 * Keeps the address in step with the name until somebody edits it themselves,
 * and then stops - retyping the name should not quietly undo a hand-written
 * address.
 */
function SlugFromName() {
  const { setValue } = useFormContext();
  const heading = useWatch({ name: 'heading' }) as string | undefined;
  const slug = useWatch({ name: 'slug' }) as string | undefined;
  const edited = useRef(false);
  const lastDerived = useRef('');

  useEffect(() => {
    if (slug != null && slug !== '' && slug !== lastDerived.current) edited.current = true;
  }, [slug]);

  useEffect(() => {
    if (edited.current) return;
    const derived = slugify(heading ?? '');
    lastDerived.current = derived;
    setValue('slug', derived, { shouldDirty: false });
  }, [heading, setValue]);

  return null;
}

/** What the product's address on the shop will look like once saved. */
function AddressPreview() {
  const type = useWatch({ name: 'type' }) as string | undefined;
  const slug = useWatch({ name: 'slug' }) as string | undefined;

  const segment = type === 'printer' ? 'tlaciaren' : type === 'toner' ? 'toner' : 'produkt';
  const producers = 'vyrobca';

  return (
    <Stack spacing={0.5} sx={{ width: '100%', mt: 1 }}>
      <Typography variant="caption" color="text.secondary">Adresa na e-shope</Typography>
      <Box
        component="code"
        sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '.85rem' }}
      >
        /{segment}/{producers}/{slug === '' || slug == null ? '…' : slug}-<i>číslo</i>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Výrobcu a číslo doplní systém. Adresa sa dá zmeniť aj neskôr.
      </Typography>
    </Stack>
  );
}

export function ProductCreate() {
  const notify = useNotify();
  const redirect = useRedirect();

  return (
    <Create
      title="Nový produkt"
      mutationOptions={{
        onSuccess: (data: { id: number }) => {
          notify('Produkt vytvorený. Doplňte cenu a zaradenie.', { type: 'info' });
          // Straight into the edit screen: a product with no price is not
          // finished, and that is where the price is set.
          redirect('edit', 'products', data.id);
        }
      }}
      actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}
    >
      <SimpleForm>
        <SlugFromName />

        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Na e-shope sa produkt objaví <b>až po zadaní bežnej ceny</b> — dovtedy je pre zákazníkov
          skrytý. Hneď po uložení sa otvorí karta produktu, kde doplníte cenu, zaradenie do
          kategórií a obrázky.
        </Alert>

        <SelectInput
          source="type"
          label="Typ produktu"
          choices={TYPES}
          validate={required()}
          helperText="Určuje, kam produkt patrí a akú má adresu."
          fullWidth
        />
        <TextInput
          source="heading"
          label="Názov"
          validate={required()}
          helperText="Tak, ako sa zobrazí zákazníkovi."
          fullWidth
        />
        <TextInput
          source="code"
          label="Kód produktu"
          helperText="Objednávacie označenie, napr. „7FP22UE 308e“."
          fullWidth
        />

        <ReferenceInput source="producerId" reference="producers" perPage={100} sort={{ field: 'name', order: 'ASC' }}>
          <AutocompleteInput
            label="Výrobca"
            optionText="name"
            helperText="Objaví sa v adrese produktu a v rozcestníku podľa výrobcov."
            fullWidth
          />
        </ReferenceInput>

        <CategoryInput />

        <TextInput source="slug" label="Adresa (slug)" helperText="Vytvorí sa z názvu; možno prepísať." fullWidth />
        <AddressPreview />

        <SelectInput
          source="warehouseStatus"
          label="Dostupnosť"
          choices={WAREHOUSE_STATUSES}
          helperText="Ak nevyberiete, dostupnosť sa nezobrazí."
          fullWidth
        />
        <TextInput source="ean" label="EAN" helperText="Čiarový kód, ak ho poznáte." fullWidth />
      </SimpleForm>
    </Create>
  );
}

/**
 * Where on the shop the product will be found.
 *
 * Offers only the categories the storefront actually browses for the chosen
 * type - the API returns that as `browsableFor`, derived from the same id list
 * the storefront hardcodes. Filing a toner under a printer category would
 * produce a product that exists, has a price and a working address, and
 * appears in no tab at all.
 */
function CategoryInput() {
  const type = useWatch({ name: 'type' }) as string | undefined;
  const { data, isPending } = useGetList('categories', { pagination: { page: 1, perPage: 100 } });

  const choices = (data ?? [])
    .filter(category => type != null && (category['browsableFor'] as string[] | undefined)?.includes(type))
    .map(category => ({ id: category.id as number, name: category['name'] as string }));

  if (type == null) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
        Zaradenie vyberiete po zvolení typu produktu.
      </Typography>
    );
  }

  return (
    <SelectInput
      source="categoryId"
      label="Zaradenie"
      choices={choices}
      isPending={isPending}
      validate={required()}
      helperText="Určuje, v ktorej sekcii e-shopu sa produkt nájde. Bez zaradenia sa nezobrazí v žiadnej."
      fullWidth
    />
  );
}
